<template>
    <Dialog v-model:open="opened">
        <DialogTrigger> <slot /> </DialogTrigger>
        <DialogContent v-auto-animate>
            <DialogHeader>
                <DialogTitle>
                    <span v-if="isEditing">
                        {{ $t("page_domains.edit") }}
                    </span>
                    <span v-else>
                        {{ $t("page_domains.add") }}
                    </span>
                </DialogTitle>
            </DialogHeader>

            <form @submit.prevent="onSubmit">
                <!-- Tab switcher for Personal/Team (only when creating) -->
                <div v-if="!isEditing" class="px-6 pt-6">
                    <Tabs
                        v-auto-animate
                        :default-value="getDefaultTab()"
                        v-model="selectedTab"
                    >
                        <TabsList class="grid w-full grid-cols-2">
                            <TabsTrigger
                                class="gap-2 rounded-l-lg"
                                value="personal"
                            >
                                <template #icon>
                                    <User
                                        :size="20"
                                        :class="[
                                            selectedTab === 'personal'
                                                ? 'text-z-primary-600'
                                                : '',
                                        ]"
                                        stroke-width="2.25"
                                    />
                                </template>
                                {{ $t("page_domains.personal_domain") }}
                            </TabsTrigger>
                            <TabsTrigger
                                class="gap-2 rounded-r-lg"
                                value="team"
                            >
                                <template #icon>
                                    <Users
                                        :size="20"
                                        :class="[
                                            selectedTab === 'team'
                                                ? 'text-z-primary-600'
                                                : '',
                                        ]"
                                        stroke-width="2.25"
                                    />
                                </template>
                                {{ $t("page_domains.team_domain") }}
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent v-auto-animate value="personal">
                            <div class="mt-6">
                                <label
                                    for="page-domain-domain-personal"
                                    class="text-sm font-medium text-iron-700 block"
                                >
                                    {{ $t("page_domains.domain") }}
                                </label>
                                <div class="mt-1 w-full">
                                    <Input
                                        id="page-domain-domain-personal"
                                        v-model="domain"
                                        type="text"
                                        data-cy="page-domain-input"
                                        name="page-domain-domain"
                                        :placeholder="
                                            $t(
                                                'page_domains.domain_placeholder',
                                            )
                                        "
                                    />
                                    <p
                                        v-if="errors.domain"
                                        class="mt-1 text-sm text-red-600"
                                    >
                                        {{ errors.domain }}
                                    </p>
                                </div>
                            </div>

                            <div class="mt-4">
                                <label class="gap-2 flex items-center">
                                    <input
                                        v-model="isDefault"
                                        type="checkbox"
                                        class="h-4 w-4 rounded border-iron-300 text-z-orange-600 focus:ring-z-orange-500"
                                    />
                                    <span class="text-sm text-iron-700">
                                        {{ $t("page_domains.is_default") }}
                                    </span>
                                </label>
                            </div>
                        </TabsContent>

                        <TabsContent v-auto-animate value="team">
                            <div v-if="!userHasTeams">
                                <Alert class="mt-6">
                                    {{ $t("page_domains.no_team") }}
                                </Alert>
                            </div>
                            <div v-if="userHasTeams">
                                <div class="mt-6">
                                    <label
                                        for="page-domain-team"
                                        class="text-sm font-medium text-iron-700 block"
                                    >
                                        {{ $t("page_domains.team") }}
                                    </label>
                                    <div class="mt-1 w-full">
                                        <Select v-model="teamIdString">
                                            <SelectTrigger>
                                                <SelectValue
                                                    :placeholder="
                                                        $t(
                                                            'page_domains.select_team',
                                                        )
                                                    "
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectGroup>
                                                    <SelectItem
                                                        v-for="team in nonPersonalTeams"
                                                        :key="team.id"
                                                        :value="String(team.id)"
                                                    >
                                                        {{ team.name }}
                                                    </SelectItem>
                                                </SelectGroup>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div class="mt-4">
                                    <label
                                        for="page-domain-domain-team"
                                        class="text-sm font-medium text-iron-700 block"
                                    >
                                        {{ $t("page_domains.domain") }}
                                    </label>
                                    <div class="mt-1 w-full">
                                        <Input
                                            id="page-domain-domain-team"
                                            v-model="domain"
                                            type="text"
                                            data-cy="page-domain-input"
                                            name="page-domain-domain"
                                            :placeholder="
                                                $t(
                                                    'page_domains.domain_placeholder',
                                                )
                                            "
                                        />
                                        <p
                                            v-if="errors.domain"
                                            class="mt-1 text-sm text-red-600"
                                        >
                                            {{ errors.domain }}
                                        </p>
                                    </div>
                                </div>

                                <div class="mt-4">
                                    <label class="gap-2 flex items-center">
                                        <input
                                            v-model="isDefault"
                                            type="checkbox"
                                            class="h-4 w-4 rounded border-iron-300 text-z-orange-600 focus:ring-z-orange-500"
                                        />
                                        <span class="text-sm text-iron-700">
                                            {{ $t("page_domains.is_default") }}
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>

                <!-- Simple form for editing -->
                <div v-else class="mt-2 px-6">
                    <div class="mt-4">
                        <label
                            for="page-domain-domain-edit"
                            class="text-sm font-medium text-iron-700 block"
                        >
                            {{ $t("page_domains.domain") }}
                        </label>
                        <div class="mt-1 w-full">
                            <Input
                                id="page-domain-domain-edit"
                                v-model="domain"
                                type="text"
                                data-cy="page-domain-input"
                                name="page-domain-domain"
                                :placeholder="
                                    $t('page_domains.domain_placeholder')
                                "
                            />
                            <p
                                v-if="errors.domain"
                                class="mt-1 text-sm text-red-600"
                            >
                                {{ errors.domain }}
                            </p>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <div class="gap-3 py-3 flex justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            @click="closeModal"
                        >
                            {{ $t("cancel") }}
                        </Button>
                        <Button
                            type="submit"
                            :disabled="
                                !isValid ||
                                (selectedTab === 'team' && !userHasTeams)
                            "
                        >
                            <span v-if="isEditing">
                                {{ $t("update") }}
                            </span>
                            <span v-else>
                                {{ $t("create") }}
                            </span>
                        </Button>
                    </div>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { ref, watch, computed } from "vue";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog-v2";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs-v2";
import { Alert } from "@/components/ui/alert";
import { User, Users } from "lucide-vue-next";
import { vAutoAnimate } from "@formkit/auto-animate/vue";
import { usePageDomainForm } from "@/composables/usePageDomainForm";
import { usePage } from "@inertiajs/vue3";
import type { Team } from "@/Pages/Zaptime/App/Teams/types/Team";

const props = defineProps<{
    pageDomainId?: number;
    pageDomainDomain?: string;
    pageDomainIsDefault?: boolean;
}>();

const opened = ref(false);
const page = usePage();
const requestedTeamId = Number(
    new URLSearchParams(page.url.split("?")[1] ?? "").get("team"),
);
const selectedTab = ref<"personal" | "team">("personal");

const personalTeam = computed(() => {
    return page.props.personalTeam as Team;
});

// Filter to teams where user is owner or admin (excluding personal team)
const nonPersonalTeams = computed(() => {
    const userId = page.props.userId as number;
    return (page.props.allTeams as Team[]).filter((team) => {
        if (team.id === personalTeam.value?.id) {
            return false;
        }

        // User owns the team via user_id
        if (team.user_id === userId) {
            return true;
        }

        // User has admin role via membership
        const role = team.membership?.role ?? "";
        return role === "admin";
    });
});

const userHasTeams = computed(() => {
    return nonPersonalTeams.value.length > 0;
});

const {
    domain,
    isDefault,
    teamId,
    errors,
    isValid,
    isEditing,
    onSubmit,
    resetForm,
} = usePageDomainForm({
    pageDomain: {
        id: props.pageDomainId,
        domain: props.pageDomainDomain,
        is_default: props.pageDomainIsDefault,
    },
    defaultTeamId: personalTeam.value?.id ?? null,
    onSuccess: () => {
        closeModal();
    },
});

// Convert between string (for Select) and number (for form)
const teamIdString = computed({
    get: () => (teamId.value ? String(teamId.value) : ""),
    set: (val: string) => {
        teamId.value = val ? parseInt(val, 10) : null;
    },
});

// Watch tab changes to update teamId
watch(selectedTab, (newTab) => {
    if (newTab === "personal") {
        teamId.value = personalTeam.value?.id ?? null;
    } else {
        if (nonPersonalTeams.value.length > 0) {
            teamId.value = nonPersonalTeams.value[0].id;
        }
    }
});

const closeModal = () => {
    opened.value = false;
};

function getDefaultTab(): "personal" | "team" {
    return requestedTeamId > 0 && requestedTeamId !== personalTeam.value?.id
        ? "team"
        : "personal";
}

// Reset form when dialog opens
watch(opened, (isOpen) => {
    if (isOpen) {
        selectedTab.value = getDefaultTab();
        const defaultTeamId =
            selectedTab.value === "team" &&
            nonPersonalTeams.value.some((team) => team.id === requestedTeamId)
                ? requestedTeamId
                : (personalTeam.value?.id ?? null);
        resetForm({
            values: {
                domain: props.pageDomainDomain ?? "",
                is_default: props.pageDomainIsDefault ?? false,
                team_id: defaultTeamId,
            },
        });
    }
});
</script>
